// Catálogo de herramientas del chat de Atlas. A propósito CHICO: este chat
// conversa y analiza, no dispara generaciones (eso vive en el Asistente de
// "Crear desde cero"). Ninguna herramienta de acá tiene efecto fuera del chat:
// lo que devuelven se guarda en el mensaje y listo.

import { GUIA_ESTILOS_PARA_MODELO } from './documentoGrafico';
import type { HerramientaChat, InterruptoresChat } from './tipos';

export const NOMBRE_HERRAMIENTA_GRAFICA = 'mostrar_respuesta_grafica';
export const NOMBRE_HERRAMIENTA_SUGERENCIAS = 'sugerir_acciones';

/** Tope de HTML por gráfico. Un modelo puede escupir un documento enorme y esto
 *  viaja al sessionStorage (cuota ~5MB) y al historial del próximo turno. */
export const LIMITE_HTML_GRAFICO = 60_000;
/** Tope de sugerencias por turno (chips). */
export const MAX_SUGERENCIAS = 4;

/**
 * Respuestas gráficas. La descripción es larga a propósito: sin un "cuándo NO"
 * explícito, el modelo la llama en CADA turno y el chat se convierte en una
 * seguidilla de gráficos innecesarios.
 */
export const HERRAMIENTA_RESPUESTA_GRAFICA: HerramientaChat = {
  name: NOMBRE_HERRAMIENTA_GRAFICA,
  description:
    'Muestra una RESPUESTA GRÁFICA (un bloque HTML que se dibuja dentro del chat) cuando la pregunta se contesta MUCHO mejor con algo visual que con prosa.\n' +
    'USALA SOLO si se cumple al menos una: (a) hay una COMPARACIÓN entre 3 o más ítems (productos, competidores, meses, canales); (b) hay una EVOLUCIÓN en el tiempo; (c) hay una COMPOSICIÓN / reparto de un total (porcentajes, participación); (d) hay una TABLA de datos que en texto plano se lee horrible (3+ filas × 3+ columnas); (e) el usuario pidió explícitamente un gráfico, una tabla o un cuadro.\n' +
    'NO la uses (respondé en texto, es lo normal): saludos, preguntas de una sola línea, explicaciones, ideas creativas, listas de menos de 3 ítems, un solo número o dato suelto, cualquier cosa donde el gráfico sea decorativo. Ante la duda, NO la uses: el default de este chat es responder escribiendo.\n' +
    'NUNCA inventes números para poder graficar. Si no tenés datos reales del usuario, no grafiques.\n' +
    'La usás UNA vez por turno como mucho, y SIEMPRE acompañada de texto: primero (o después) escribí en dos o tres líneas la conclusión — el gráfico ilustra, no reemplaza a la respuesta.\n' +
    'Devuelve `{ mostrado: true }` cuando quedó visible en el chat.',
  input_schema: {
    type: 'object',
    properties: {
      titulo: {
        type: 'string',
        description:
          'Título corto de lo que se muestra (máximo ~60 caracteres). Ej: "Ventas por canal — últimos 6 meses".',
      },
      html: {
        type: 'string',
        // La guía de estilos NO se escribe acá: sale de `documentoGrafico.ts`,
        // que es el archivo que define la hoja de verdad que se le inyecta al
        // marco. Si estuviera duplicada, el día que alguien renombre una clase
        // el modelo seguiría pidiendo la vieja y el gráfico saldría sin estilo
        // (nadie se entera: no hay error, solo se ve feo). Un test verifica que
        // cada clase nombrada en la guía exista de verdad en la hoja.
        description: `El bloque HTML autocontenido a dibujar. ${GUIA_ESTILOS_PARA_MODELO}`,
      },
    },
    required: ['titulo', 'html'],
  },
};

/**
 * Sugerencias de continuación (chips). Sin efecto: se guardan en el mensaje y
 * la UI las ofrece como atajo para el próximo turno.
 */
export const HERRAMIENTA_SUGERIR_ACCIONES: HerramientaChat = {
  name: NOMBRE_HERRAMIENTA_SUGERENCIAS,
  description:
    'Ofrece 2 a 4 continuaciones cortas para que el usuario siga la conversación de un toque (aparecen como botones bajo tu respuesta). Usala cuando hay caminos claros y concretos a seguir. NO la uses si el usuario ya sabe qué quiere, si estás en medio de un ida y vuelta puntual, ni la repitas turno tras turno. Devuelve `{ mostrado: true }`.',
  input_schema: {
    type: 'object',
    properties: {
      acciones: {
        type: 'array',
        description:
          'Entre 2 y 4 frases cortas en primera persona del usuario (máximo ~45 caracteres cada una). Ej: ["Compará con mi competencia", "Armá un plan para septiembre"].',
        items: { type: 'string' },
      },
    },
    required: ['acciones'],
  },
};

/**
 * Qué herramientas se le ofrecen al modelo según los interruptores.
 *
 * `respuestasGraficas` es del FRONT: apagada ⇒ la herramienta gráfica NO viaja
 * en el pedido, así que el modelo ni siquiera puede intentar usarla.
 * `investigacion` y `busquedaWeb` NO agregan herramientas acá: son tools
 * server-side que adjunta el backend (ver `construirCuerpoPedido`).
 */
export function herramientasParaInterruptores(
  interruptores: InterruptoresChat,
): HerramientaChat[] {
  const lista: HerramientaChat[] = [];
  if (interruptores.respuestasGraficas) lista.push(HERRAMIENTA_RESPUESTA_GRAFICA);
  lista.push(HERRAMIENTA_SUGERIR_ACCIONES);
  // `preguntar` (el cuestionario con opciones) NO va acá: es global, vive en
  // `V2_AGENT_TOOLS` desde el merge de principal del 2026-09-12. Mandarla dos veces
  // rompe el pedido (la API exige nombres únicos) —
  // ADR-2026-09-12-preguntar-es-global-y-el-chat-no-la-duplica.
  return lista;
}

// ── Normalización de lo que devuelve el modelo ────────────────────────────
// El input de una tool lo escribe el modelo: puede venir incompleto, con tipos
// raros o gigante. Se normaliza acá (puro, testeable) y el hook solo aplica.

export interface GraficoNormalizado {
  titulo: string;
  html: string;
  /** true si hubo que recortar el HTML por pasarse del límite. */
  recortado: boolean;
}

/** Valida/recorta el input de `mostrar_respuesta_grafica`. null si no sirve. */
export function normalizarGrafico(entrada: unknown): GraficoNormalizado | null {
  if (!entrada || typeof entrada !== 'object') return null;
  const e = entrada as Record<string, unknown>;
  const html = typeof e.html === 'string' ? e.html.trim() : '';
  if (!html) return null;
  const titulo =
    typeof e.titulo === 'string' && e.titulo.trim()
      ? e.titulo.trim().slice(0, 80)
      : 'Respuesta gráfica';
  const recortado = html.length > LIMITE_HTML_GRAFICO;
  return {
    titulo,
    html: recortado ? html.slice(0, LIMITE_HTML_GRAFICO) : html,
    recortado,
  };
}

/** Valida/recorta el input de `sugerir_acciones`. Array vacío si no sirve. */
export function normalizarSugerencias(entrada: unknown): string[] {
  if (!entrada || typeof entrada !== 'object') return [];
  const acciones = (entrada as Record<string, unknown>).acciones;
  if (!Array.isArray(acciones)) return [];
  return acciones
    .filter((a): a is string => typeof a === 'string')
    .map((a) => a.trim())
    .filter((a) => a.length > 0)
    .slice(0, MAX_SUGERENCIAS);
}

/** Qué se le contesta al modelo por cada herramienta (el `tool_result`). */
export function resultadoHerramienta(
  nombre: string,
  entrada: unknown,
): Record<string, unknown> {
  if (nombre === NOMBRE_HERRAMIENTA_GRAFICA) {
    const g = normalizarGrafico(entrada);
    return g
      ? { mostrado: true, titulo: g.titulo, ...(g.recortado ? { recortado: true } : {}) }
      : {
          mostrado: false,
          motivo: 'El html vino vacío. Respondé en texto o mandá el html completo.',
        };
  }
  if (nombre === NOMBRE_HERRAMIENTA_SUGERENCIAS) {
    const s = normalizarSugerencias(entrada);
    return s.length > 0
      ? { mostrado: true, cantidad: s.length }
      : { mostrado: false, motivo: 'No llegó ninguna acción válida.' };
  }
  return {
    mostrado: false,
    motivo: `La herramienta "${nombre}" no existe en este chat. Respondé en texto.`,
  };
}
